import { useState, useEffect } from 'react';
import { Popover, Box, IconButton, Checkbox, FormControlLabel, Divider, Slider, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';

const PRESETS = [25, 50, 75, 100];

/**
 * Parses the stored width string into { pct, useNatural }.
 * - "400px" → useNatural=true, pct = round(400 / naturalWidth * 100)
 * - "50%"  → useNatural=false, pct = 50
 * Falls back to { pct: 100, useNatural: true } when no width is set.
 * @param {string|null} currentWidth
 * @param {number} naturalWidth
 * @returns {{ pct: number, useNatural: boolean }}
 */
function parseCurrentWidth(currentWidth, naturalWidth) {
    if (!currentWidth) return { pct: 100, useNatural: true };

    if (currentWidth.endsWith('px')) {
        const px = parseFloat(currentWidth);
        if (!isNaN(px) && naturalWidth > 0) {
            return { pct: Math.round(px / naturalWidth * 100), useNatural: true };
        }
    }

    if (currentWidth.endsWith('%')) {
        const pct = parseFloat(currentWidth);
        if (!isNaN(pct)) {
            return { pct, useNatural: false };
        }
    }

    return { pct: 100, useNatural: true };
}

/**
 * ImageContextMenu — right-click context menu for resizing images in the BodyEditor.
 * @param {object} props
 * @param {import('@tiptap/react').Editor} props.editor
 * @param {{ x: number, y: number }} props.pos - screen position for the popover
 * @param {number|null} props.imgNodePos - ProseMirror position of the image node (stable across re-renders)
 * @param {number} props.imgNaturalWidth - natural pixel width captured at right-click time
 * @param {string|null} props.imgCurrentWidth - current stored width ("400px" / "50%" / null)
 * @param {boolean} props.open
 * @param {function} props.onClose
 */
export default function ImageContextMenu({ editor, pos, imgNodePos, imgNaturalWidth, imgCurrentWidth, open, onClose }) {
    const [sliderValue, setSliderValue] = useState(100);
    const [useNatural, setUseNatural] = useState(true);
    const [limitTo100, setLimitTo100] = useState(true);

    // Sync slider + useNatural to the image's current width whenever the menu opens.
    useEffect(() => {
        if (!open) return;
        const { pct, useNatural: nat } = parseCurrentWidth(imgCurrentWidth, imgNaturalWidth);
        setUseNatural(nat);
        setSliderValue(pct);
        setLimitTo100(pct <= 100);
    }, [open, imgCurrentWidth, imgNaturalWidth]);

    const sliderMax = limitTo100 ? 100 : 400;

    /**
     * Applies a percentage resize to the image node at imgNodePos.
     * @param {number} pct - percentage
     * @param {boolean} close - whether to close the menu after applying
     */
    const applyResize = (pct, close = true) => {
        if (!editor || imgNodePos == null) return;

        const width = useNatural
            ? `${Math.round(imgNaturalWidth * pct / 100)}px`
            : `${pct}%`;

        editor.chain()
            .setNodeSelection(imgNodePos)
            .updateAttributes('image', { width })
            .run();

        if (close) onClose();
    };

    const handleSliderChange = (_, val) => setSliderValue(val);
    const handleSliderCommit = (_, val) => applyResize(val, false);

    const handleLimitTo100 = (e) => {
        setLimitTo100(e.target.checked);
        if (e.target.checked && sliderValue > 100) setSliderValue(100);
    };

    return (
        <Popover
            open={open}
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={pos ? { top: pos.y, left: pos.x } : undefined}
            style={{ zIndex: 2000 }}
            PaperProps={{
                sx: {
                    background: '#0d1f33',
                    border: '0.5px solid #294766',
                    borderRadius: '6px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                    p: '10px 12px',
                    width: 240,
                },
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
                <span style={{ color: '#7fb3d3', fontSize: 11, fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                    Resize image
                </span>
                <IconButton size="small" onClick={onClose} sx={{ color: '#294766', p: '2px', '&:hover': { color: '#ff6b6b' } }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Box>

            {/* Checkboxes */}
            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        checked={useNatural}
                        onChange={(e) => setUseNatural(e.target.checked)}
                        sx={{ color: '#294766', '&.Mui-checked': { color: '#4cceac' }, p: '2px 4px' }}
                    />
                }
                label={<span style={{ color: '#7fb3d3', fontSize: 11, fontFamily: 'DM Sans, sans-serif' }}>% of natural width</span>}
                sx={{ display: 'block', mb: '2px', ml: 0 }}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        checked={limitTo100}
                        onChange={handleLimitTo100}
                        sx={{ color: '#294766', '&.Mui-checked': { color: '#4cceac' }, p: '2px 4px' }}
                    />
                }
                label={<span style={{ color: '#7fb3d3', fontSize: 11, fontFamily: 'DM Sans, sans-serif' }}>Limit to 100%</span>}
                sx={{ display: 'block', mb: '4px', ml: 0 }}
            />

            <Divider sx={{ borderColor: '#294766', mb: '10px' }} />

            {/* Preset buttons */}
            <Box sx={{ display: 'flex', gap: '6px', mb: '12px' }}>
                {PRESETS.map((pct) => (
                    <Tooltip key={pct} title={`${pct}%`} componentsProps={tooltipSxElevated} placement="top">
                        <button
                            onClick={() => applyResize(pct, false)}
                            style={{
                                background: '#0b1e2e',
                                border: '0.5px solid #294766',
                                borderRadius: '4px',
                                color: '#7fb3d3',
                                fontSize: 11,
                                fontFamily: 'DM Sans, sans-serif',
                                padding: '3px 8px',
                                cursor: 'pointer',
                                flex: 1,
                            }}
                            onMouseEnter={(e) => { e.target.style.borderColor = '#66b2ff'; e.target.style.color = '#a3d1ff'; }}
                            onMouseLeave={(e) => { e.target.style.borderColor = '#294766'; e.target.style.color = '#7fb3d3'; }}
                        >
                            {pct}%
                        </button>
                    </Tooltip>
                ))}
            </Box>

            {/* Slider + value label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '8px' }}>
                <Slider
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                    min={5}
                    max={sliderMax}
                    step={5}
                    sx={{
                        flex: 1,
                        color: '#4cceac',
                        '& .MuiSlider-thumb': { width: 12, height: 12 },
                        '& .MuiSlider-rail': { color: '#294766' },
                    }}
                />
                <span style={{ color: '#a3d1ff', fontSize: 11, fontFamily: 'DM Sans, sans-serif', minWidth: 36, textAlign: 'right' }}>
                    {sliderValue}%
                </span>
            </Box>

            {/* Apply + close */}
            <button
                onClick={() => applyResize(sliderValue, true)}
                style={{
                    width: '100%',
                    background: '#0b1e2e',
                    border: '0.5px solid #4cceac',
                    borderRadius: '4px',
                    color: '#4cceac',
                    fontSize: 11,
                    fontFamily: 'DM Sans, sans-serif',
                    padding: '4px 0',
                    cursor: 'pointer',
                }}
            >
                Apply & close
            </button>
        </Popover>
    );
}
